/**
 * The spotlight board, from the moderator's side.
 *
 * Evan, 2026-09-22: *"we should have an admin switch for it and detailed management of it from
 * admin dash. under search page."* The switches live one level up, in the card table of
 * `SpotlightPanel` — they belong beside "Live" because they are properties of a card. THIS file is
 * the "detailed management" half: one card's messages, which night each belongs to, who wrote it,
 * and a takedown you can undo.
 *
 * ── ⚠️ EVERYTHING GOES THROUGH RPCs. NEVER READ THE TABLE. ───────────────────────────────────
 * `spotlight_board_messages` carries NO `anon` / `authenticated` grants, deliberately — Supabase's
 * default privileges had granted them and they were revoked, because a direct SELECT is the one
 * thing that would unmask an anonymous author to anybody holding the anon key (which is every copy
 * of the phone app). This dashboard authenticates as an ordinary Supabase user on that same anon
 * key; its privilege comes entirely from `admin_list_spotlight_board` /
 * `admin_hide_spotlight_board_message` being SECURITY DEFINER behind `_assert_admin_caller()`. A
 * `supabase.from('spotlight_board_messages')` here would not merely be bad style — it would fail,
 * and the correct fix would never be to add the grant.
 *
 * ── ⚠️ YOU SEE THE AUTHOR. READERS DO NOT. ───────────────────────────────────────────────────
 * `admin_list_spotlight_board` ignores `is_anonymous` and returns the real `user_id` + `username`,
 * because a moderator who cannot see who is posting cannot moderate. The reader-facing
 * `get_spotlight_board` nulls all three for somebody else's anonymous message. Every anonymous row
 * below therefore says so IN PLACE, next to the name: the failure this guards against is future-you
 * reading a name off this screen and assuming it was public.
 *
 * ── ⚠️ HIDING IS NEVER DELETING ──────────────────────────────────────────────────────────────
 * `admin_hide_spotlight_board_message` stamps `hidden_at` / `hidden_by` and nothing else. The row
 * stays, it drops out of every reader's board on their next fetch, and the same call with
 * `p_hidden = false` puts it back. So the list here keeps hidden messages visible to the moderator
 * rather than making them vanish — a takedown you cannot see is a takedown you cannot review.
 *
 * ── ⚠️ WHAT NIGHT IT IS COMES FROM THE SERVER ────────────────────────────────────────────────
 * `public.spotlight_poll_day()` — the same STABLE function that stamps `poll_day` on every message
 * and that the app's card reads. A poll day runs 08:00 → 08:00 America/New_York, so "tonight" at
 * 2am is still yesterday's date, and a browser in another zone (or one whose clock is an hour out)
 * that worked it out locally would draw the wrong night's header over the right night's messages.
 * There is exactly one authority for this and it is not the client. It is `anon`-executable, so the
 * call costs nothing and cannot fail for permission reasons.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/useToast';
import { EmptyState } from '../ui/EmptyState';
import { ErrorBanner } from '../ui/ErrorBanner';
import { SkeletonRows } from '../ui/Skeleton';
import { errText, rpcMissing } from '../lib/collectionKinds';

/**
 * One message exactly as `admin_list_spotlight_board` builds it. Verified against
 * `pg_get_functiondef` on production 2026-09-22 — the repo's migrations may lag, the function body
 * does not.
 */
export type BoardMessage = {
  id: string;
  body: string;
  /** ISO. Server time, formatted into the reader's local zone for display only. */
  created_at: string | null;
  /** `YYYY-MM-DD` — WHICH NIGHT, 8am-to-8am ET. Stamped by trigger, never by a client. */
  poll_day: string | null;
  /** True = readers see no name on it. You still see one; see the file header. */
  is_anonymous: boolean;
  /** Non-null = taken down. Reversible. */
  hidden_at: string | null;
  user_id: string | null;
  username: string | null;
};

/**
 * ⚠️ MATCHES THE RPC'S OWN DEFAULT, not its ceiling (which is 500). A board that has run past 200
 * messages in one card's lifetime is a different problem from the one this screen solves, and the
 * footer says plainly when the list is truncated rather than quietly showing a prefix.
 */
const BOARD_LIMIT = 200;

const BOARD_SETUP_NOTE =
  'Board moderation is not set up on this database yet — apply ' +
  'supabase/migrations/20260922000002_spotlight_board.sql and reload. Until then the switch in the ' +
  'table above has nothing to read and this panel cannot list anything.';

type Filter = 'all' | 'visible' | 'hidden';

function normalizeMessages(raw: unknown): BoardMessage[] {
  if (!Array.isArray(raw)) return [];
  return (raw as Partial<BoardMessage>[]).map((m) => ({
    id: String(m.id ?? ''),
    body: String(m.body ?? ''),
    created_at: typeof m.created_at === 'string' ? m.created_at : null,
    poll_day: typeof m.poll_day === 'string' ? m.poll_day : null,
    is_anonymous: Boolean(m.is_anonymous),
    hidden_at: typeof m.hidden_at === 'string' ? m.hidden_at : null,
    user_id: typeof m.user_id === 'string' ? m.user_id : null,
    username: typeof m.username === 'string' && m.username.trim() !== '' ? m.username : null,
  }));
}

/**
 * The clock, not the calendar. The date is already the group header above every row, so repeating
 * it on each line would bury the one thing that separates two messages on the same night.
 */
function fmtClock(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * A poll day as a person reads it. `YYYY-MM-DD` is parsed as UTC noon rather than midnight so the
 * label cannot slide a day backwards for a viewer west of Greenwich — this is a DATE, not an
 * instant, and it must print as itself in every zone.
 */
function fmtNight(day: string): string {
  const d = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function SpotlightBoardPanel({
  cardId,
  cardBody,
  boardEnabled,
  resetsDaily,
  onClose,
  onModerated,
}: {
  cardId: string;
  cardBody: string;
  /** Drives the empty states: "nobody can post" and "nobody has posted" are different facts. */
  boardEnabled: boolean;
  /** When true the reader's board only holds tonight, so the grouping below is load-bearing. */
  resetsDaily: boolean;
  onClose: () => void;
  /** Parent refresh — `board_count` in the table is a server number and must not be guessed here. */
  onModerated: () => void;
}) {
  const [rows, setRows] = useState<BoardMessage[]>([]);
  /** Null until the server answers. NEVER computed here — see the file header. */
  const [today, setToday] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [setupNote, setSetupNote] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  /** Which row's hide/restore is in flight — per row, so one takedown does not freeze the list. */
  const [busyId, setBusyId] = useState<string | null>(null);
  const { showError, showSuccess, toastNode } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      /* Both in parallel: neither answer depends on the other and the night label is needed for
       * the first paint, not after it. */
      const [board, day] = await Promise.all([
        supabase.rpc('admin_list_spotlight_board', { p_card_id: cardId, p_limit: BOARD_LIMIT }),
        supabase.rpc('spotlight_poll_day'),
      ]);
      if (board.error) {
        if (rpcMissing(board.error.message, 'admin_list_spotlight_board')) {
          setSetupNote(BOARD_SETUP_NOTE);
          setRows([]);
          return;
        }
        throw new Error(board.error.message);
      }
      setSetupNote(null);
      setRows(normalizeMessages(board.data));
      /* A failed poll-day call costs the "Tonight" label and nothing else — the messages are still
       * grouped by the night the SERVER stamped on each of them. */
      setToday(!day.error && typeof day.data === 'string' ? day.data : null);
    } catch (e: unknown) {
      setError(errText(e, 'Could not load this board'));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [cardId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * ⚠️ THE ROW IS PATCHED FROM THE RPC'S OWN RETURN, NOT RELOADED. `admin_hide_spotlight_board_message`
   * hands back the `hidden_at` it just wrote, so the badge shows the server's stamp rather than a
   * local `new Date()` that would be a few hundred milliseconds off. Reloading the whole board
   * instead would throw away the reader's scroll position mid-moderation, which on a long night is
   * the difference between reviewing a board and hunting for your place in it.
   */
  const setHidden = useCallback(
    async (m: BoardMessage, hidden: boolean) => {
      setBusyId(m.id);
      try {
        const { data, error: e } = await supabase.rpc('admin_hide_spotlight_board_message', {
          p_id: m.id,
          p_hidden: hidden,
        });
        if (e) throw new Error(e.message);
        const stamped =
          data && typeof data === 'object' && typeof (data as { hidden_at?: unknown }).hidden_at === 'string'
            ? ((data as { hidden_at: string }).hidden_at)
            : null;
        setRows((prev) =>
          prev.map((r) =>
            r.id === m.id ? { ...r, hidden_at: hidden ? (stamped ?? new Date().toISOString()) : null } : r,
          ),
        );
        showSuccess(
          hidden
            ? 'Hidden — it drops off every reader’s board. Nothing was deleted.'
            : 'Restored — it is back on the board.',
        );
        onModerated();
      } catch (e: unknown) {
        showError(errText(e, 'Could not change that message'));
      } finally {
        setBusyId(null);
      }
    },
    [onModerated, showError, showSuccess],
  );

  const hiddenCount = useMemo(() => rows.filter((r) => r.hidden_at !== null).length, [rows]);
  const visibleCount = rows.length - hiddenCount;

  const filtered = useMemo(() => {
    if (filter === 'visible') return rows.filter((r) => r.hidden_at === null);
    if (filter === 'hidden') return rows.filter((r) => r.hidden_at !== null);
    return rows;
  }, [filter, rows]);

  /**
   * By night, newest first. `YYYY-MM-DD` sorts lexicographically exactly as it sorts
   * chronologically, so no dates are parsed to order them — which also means a row with a null
   * `poll_day` (impossible today; the column has a default and a trigger) sorts last under its own
   * heading instead of silently joining a night it does not belong to.
   */
  const groups = useMemo(() => {
    const byDay = new Map<string, BoardMessage[]>();
    for (const m of filtered) {
      const key = m.poll_day ?? '';
      const list = byDay.get(key);
      if (list) list.push(m);
      else byDay.set(key, [m]);
    }
    return Array.from(byDay.entries()).sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0));
  }, [filtered]);

  /**
   * ⚠️ MEASURED ON THE UNFILTERED ROWS. "Nothing tonight" is a fact about the board, not about
   * whichever filter pill happens to be pressed — computing it from `filtered` would announce an
   * empty night every time you asked to see only the hidden messages.
   */
  const tonightIsEmpty = today !== null && !rows.some((r) => r.poll_day === today);
  const hasHistory = rows.length > 0;

  return (
    <section className="mt-8 rounded-xl border border-border bg-surface p-5">
      {toastNode}

      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="admin-label-primary text-base">Board · {cardBody}</h2>
          <div className="text-[11px] text-mutedDim">
            {boardEnabled ? (
              <>
                On — readers can post under this card.
                {resetsDaily
                  ? ' It clears at 8am ET, so they only ever see tonight.'
                  : ' It does not reset, so they see every message at once.'}
              </>
            ) : (
              'Off — nobody can see or add to this board. Anything below is history.'
            )}
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            className="admin-btn admin-btn-secondary admin-btn-sm"
            disabled={loading}
            onClick={() => void load()}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </div>

      {/*
        ⚠️ SAID ONCE, AT THE TOP, WHERE IT CANNOT BE MISSED. Every name below is admin-only. The
        row-level "Anonymous to readers" badge repeats it where the name actually is, because this
        line will be scrolled past.
      */}
      <div className="mb-4 max-w-3xl rounded-md border border-border/90 bg-white/[0.025] px-3 py-2 text-[11px] leading-relaxed text-muted">
        You are shown who wrote every message, including the anonymous ones — that is what makes
        moderation possible. Readers are not: on the phone an anonymous message carries no name, no
        handle and no avatar. Hiding is reversible and deletes nothing.
      </div>

      {error ? <ErrorBanner className="mb-4">{error}</ErrorBanner> : null}
      {setupNote ? (
        <div className="mb-4 max-w-3xl rounded-md border border-border/90 bg-white/[0.025] px-3 py-2 text-[11px] leading-relaxed text-muted">
          {setupNote}
        </div>
      ) : null}

      {/*
        ── WHERE THE REPORT QUEUE GOES ────────────────────────────────────────────────────────────
        A reader-facing "report this message" control and its admin surface are being built
        separately (OUTSTANDING 66-C item 4). It belongs HERE, above the full list: a moderator
        opens this panel to deal with what has been flagged, and reads the whole board only when
        nothing has been. `admin_list_spotlight_board_reports(p_card_id, p_limit, p_include_hidden)`
        already exists on production and returns `{auto_hide_threshold, reports}` — but nothing in
        this file calls it, on purpose, because that section's shape is not this session's to
        decide. Slot it in above this comment and the list below needs no change; a reported message
        is an ordinary row down there and its Hide button already works.
      */}

      {loading ? (
        <SkeletonRows rows={5} />
      ) : !hasHistory ? (
        /*
         * ⚠️ THREE DIFFERENT NOTHINGS, AND THEY MUST NOT LOOK ALIKE. "There is no board" is a
         * setting you can change in one click; "the board is live and nobody has typed" is a
         * product fact you can only wait out. Rendering one grey box for both is how a switch gets
         * flipped that was never off.
         */
        boardEnabled ? (
          <EmptyState
            title="The board is on and nobody has posted"
            hint={
              resetsDaily
                ? 'It is live under the card right now — readers see the empty board and its compose field. It clears at 8am ET, so this is tonight only.'
                : 'It is live under the card right now — readers see the empty board and its compose field.'
            }
          />
        ) : (
          <EmptyState
            title="This card has no board"
            hint="Readers see the card with nowhere to post. Turn on Board in the table above and it appears under the poll."
          />
        )
      ) : (
        <>
          {/* Filter — counts on the pills, so the shape of the board is legible before you click. */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {([
              ['all', 'All', rows.length],
              ['visible', 'On the board', visibleCount],
              ['hidden', 'Hidden', hiddenCount],
            ] as [Filter, string, number][]).map(([k, label, n]) => (
              <button
                key={k}
                type="button"
                className="admin-pill !h-7 !px-3 !text-[11px]"
                data-active={filter === k}
                onClick={() => setFilter(k)}
              >
                {label} · <span className="tabular-nums">{n}</span>
              </button>
            ))}
            <span className="text-[11px] text-mutedDim">
              {resetsDaily
                ? 'Grouped by night — a night runs 8am to 8am Eastern, so a 2am message belongs to the evening before.'
                : 'This card does not reset, so readers see all of these at once. The dates below are just when each was posted.'}
            </span>
            {/*
              ⚠️ STATED, NOT PAPERED OVER. Every night below is still the night the SERVER stamped
              on those messages; the only thing missing is which of them is now. Working it out here
              from the browser clock is the one shortcut this panel must not take — see the header.
            */}
            {resetsDaily && today === null ? (
              <span className="text-[11px] text-amber-400">
                Could not ask the server what night it is, so no group below is marked Tonight.
                Refresh to try again.
              </span>
            ) : null}
          </div>

          {/*
            ⚠️ THE EMPTY **TONIGHT** IS RENDERED, NOT OMITTED. On a resetting card the reader's board
            is tonight and nothing else, so "no group for today" is the single most important state
            this panel can show — and a list that simply starts at yesterday looks identical to a
            busy night until you read the date.
          */}
          {boardEnabled && resetsDaily && tonightIsEmpty && filter === 'all' ? (
            <div className="mb-4">
              <div className="mb-2 flex flex-wrap items-baseline gap-x-3">
                <h3 className="admin-section-label">Tonight{today ? ` · ${fmtNight(today)}` : ''}</h3>
                <span className="text-[11px] text-mutedDim">What a reader sees right now</span>
              </div>
              <EmptyState
                title="Nothing posted tonight"
                hint="The board reset at 8am ET. Readers see it empty; the earlier nights below are still stored but nobody can reach them."
                compact
              />
            </div>
          ) : null}

          <div className="space-y-5">
            {groups.map(([day, msgs]) => {
              const isTonight = today !== null && day === today;
              return (
                <div key={day || 'undated'}>
                  <div className="mb-2 flex flex-wrap items-baseline gap-x-3">
                    <h3 className="admin-section-label">
                      {day ? fmtNight(day) : 'No night recorded'}
                      {isTonight ? ' · Tonight' : ''}
                    </h3>
                    <span className="text-[11px] text-mutedDim">
                      {msgs.length} message{msgs.length === 1 ? '' : 's'}
                      {day ? ` · poll_day ${day}` : ''}
                    </span>
                    {isTonight && boardEnabled && resetsDaily ? (
                      <span className="admin-status admin-status-accent">On the board now</span>
                    ) : null}
                    {/*
                      ⚠️ GUARDED ON `today !== null`, NOT JUST ON `!isTonight`. If the poll-day call
                      failed, `isTonight` is false for EVERY group — and this line would then label
                      tonight's own messages as unreachable, which is the one claim on this panel
                      that could get a live board abandoned as dead.
                    */}
                    {today !== null && !isTonight && boardEnabled && resetsDaily ? (
                      <span className="text-[11px] text-mutedDim">
                        Past — no reader can see these any more.
                      </span>
                    ) : null}
                  </div>

                  <ul className="divide-y divide-border/50 overflow-hidden rounded-lg border border-border">
                    {msgs.map((m) => {
                      const hidden = m.hidden_at !== null;
                      return (
                        <li
                          key={m.id}
                          className={`flex flex-wrap items-start gap-x-4 gap-y-2 px-3 py-3 ${
                            hidden ? 'bg-black/25' : 'bg-surface'
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div
                              className={`whitespace-pre-wrap break-words text-[13px] leading-snug ${
                                hidden ? 'text-mutedDim line-through decoration-mutedDim/60' : 'text-textPrimary'
                              }`}
                            >
                              {m.body}
                            </div>
                            <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-mutedDim">
                              {/* The author. Admin-only for an anonymous row — see the file header. */}
                              <span className="text-muted">
                                {m.username ? `@${m.username}` : m.user_id ? 'no username' : 'deleted account'}
                              </span>
                              {m.is_anonymous ? (
                                <span className="admin-badge admin-badge-warning">Anonymous to readers</span>
                              ) : (
                                <span className="admin-badge admin-badge-neutral">Signed</span>
                              )}
                              <span className="tabular-nums">{fmtClock(m.created_at)}</span>
                              {hidden ? (
                                <span className="admin-badge admin-badge-danger">
                                  Hidden {fmtClock(m.hidden_at)}
                                </span>
                              ) : null}
                              {/* The handle is not always enough to act on — deleted and blank
                                  usernames both happen, and the id is what every other admin
                                  surface takes. */}
                              {m.user_id ? (
                                <span className="admin-chip-id !text-[10px]">{m.user_id.slice(0, 8)}</span>
                              ) : null}
                            </div>
                          </div>
                          <button
                            type="button"
                            className={`admin-btn admin-btn-sm shrink-0 ${
                              hidden ? 'admin-btn-secondary' : 'admin-btn-ghost'
                            }`}
                            disabled={busyId === m.id}
                            onClick={() => void setHidden(m, !hidden)}
                          >
                            {busyId === m.id ? 'Working…' : hidden ? 'Restore' : 'Hide'}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              title={filter === 'hidden' ? 'Nothing has been hidden' : 'Every message is hidden'}
              hint="Switch the filter above to see the rest."
              compact
            />
          ) : null}

          {/* Truncation is stated, not implied by a list that simply stops. */}
          {rows.length >= BOARD_LIMIT ? (
            <p className="mt-4 text-[11px] leading-relaxed text-mutedDim">
              Showing the newest {BOARD_LIMIT} messages — this card has at least that many and older
              ones are not on this page.
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}
